import type { ComponentType } from 'react';
import { Between, H, keys, W } from '../stage';

/**
 * The effector guides: what each starting recipe does to an array, shown on one.
 *
 * Not a tool and not a cursor — an effector is set on what is already there — so these are
 * pictures of the result and nothing else: a grid of copies rests, then the effect arrives
 * and holds. What is where it started is the quiet ghost; what the effect changed is in the
 * accent. No words in the picture, as everywhere in the guides.
 *
 * Every copy's move is four things, all drawn the same way — where it slides, how far it
 * turns, how much bigger it gets, how faint it goes — so a new recipe is only a function
 * from where a copy is, and which number it is, to those four.
 */

const COLS = 6;
const ROWS = 3;
const PITCH = 32;
const SIZE = 18;
const LEFT = (W - (COLS - 1) * PITCH) / 2;
const TOP = (H - (ROWS - 1) * PITCH) / 2;

/** The effect arrives between these two moments of the loop, and holds after. */
const FROM = 0.2;
const TO = 0.6;

interface Move {
  readonly dx: number;
  readonly dy: number;
  readonly turn: number;
  readonly scale: number;
  readonly opacity: number;
}

interface Cell {
  readonly x: number;
  readonly y: number;
  /** Which copy it is, from the first (0) to the last (1). */
  readonly along: number;
}

const cells: Cell[] = [];
for (let row = 0; row < ROWS; row++) {
  for (let col = 0; col < COLS; col++) {
    cells.push({ x: LEFT + col * PITCH, y: TOP + row * PITCH, along: (row * COLS + col) / (ROWS * COLS - 1) });
  }
}

/** The same numbers every time: a picture that reshuffled itself on each render would flicker. */
function jitterOf(seed: number): number {
  let hash = Math.imul(seed + 1, 2654435761);
  hash ^= hash >>> 15;
  hash = Math.imul(hash, 2246822519);
  hash ^= hash >>> 13;
  return ((hash >>> 0) / 0xffffffff) * 2 - 1;
}

const smooth = (value: number) => {
  const at = Math.min(1, Math.max(0, value));
  return at * at * (3 - 2 * at);
};

const REST: Move = { dx: 0, dy: 0, turn: 0, scale: 1, opacity: 1 };

/** One copy: where it rests, quietly, and the one that moves to where the effect puts it. */
function Copy({ cell, move, ghost = true }: { cell: Cell; move: Move; ghost?: boolean }) {
  const hold = (a: string, b: string) => keys([a, a, b, b], [0, FROM, TO, 1]);
  const half = SIZE / 2;
  return (
    <g transform={`translate(${cell.x} ${cell.y})`}>
      {ghost ? <rect className="g-obj" x={-half} y={-half} width={SIZE} height={SIZE} rx={3} /> : null}
      <g>
        <animateTransform attributeName="transform" type="translate" {...hold('0 0', `${move.dx.toFixed(2)} ${move.dy.toFixed(2)}`)} />
        <g>
          <animateTransform attributeName="transform" type="rotate" {...hold('0', move.turn.toFixed(2))} />
          <g>
            <animateTransform attributeName="transform" type="scale" {...hold('1', move.scale.toFixed(3))} />
            <rect className="g-new" x={-half} y={-half} width={SIZE} height={SIZE} rx={3}>
              <animate attributeName="opacity" {...hold('1', move.opacity.toFixed(3))} />
            </rect>
          </g>
        </g>
      </g>
    </g>
  );
}

/** Every copy in the grid, moved by whatever the recipe says of it. */
function Copies({ move, ghost }: { move: (cell: Cell, index: number) => Partial<Move>; ghost?: boolean }) {
  return (
    <>
      {cells.map((cell, index) => (
        <Copy key={index} cell={cell} move={{ ...REST, ...move(cell, index) }} ghost={ghost} />
      ))}
    </>
  );
}

/** Scatter: each copy knocked off its place, its own way. */
function ScatterDemo() {
  return (
    <Copies
      move={(_cell, index) => ({
        dx: jitterOf(index * 4) * 8,
        dy: jitterOf(index * 4 + 1) * 8,
        turn: jitterOf(index * 4 + 2) * 28,
        scale: 1 + jitterOf(index * 4 + 3) * 0.28,
      })}
    />
  );
}

/** Grow: each copy a little bigger than the one before, the last the biggest. */
function GrowDemo() {
  return <Copies move={(cell) => ({ scale: 1 + cell.along * 0.75 })} />;
}

/** Twist: each copy turned a little further than the one before. */
function TwistDemo() {
  return <Copies move={(cell) => ({ turn: cell.along * 85 })} />;
}

/** Fade: each copy a little fainter than the one before. There is nothing to leave behind, so no ghost. */
function FadeDemo() {
  return <Copies ghost={false} move={(cell) => ({ opacity: 1 - cell.along * 0.8 })} />;
}

/** Magnify: a spot on the array, and the copies in it swell, the most in the middle. */
function MagnifyDemo() {
  const cx = W / 2;
  const cy = H / 2;
  const reach = 62;
  return (
    <>
      <Copies move={(cell) => ({ scale: 1 + smooth(1 - Math.hypot(cell.x - cx, cell.y - cy) / reach) * 0.75 })} />
      <g>
        <Between from={0.1} to={0.97} />
        <ellipse className="g-band" cx={cx} cy={cy} rx={reach} ry={reach * 0.78} />
        <rect className="g-handle" x={cx - 2.5} y={cy - 2.5} width={5} height={5} />
      </g>
    </>
  );
}

/** By the key the guide system asks for: `effector:` and the recipe's own id. */
export const EFFECTOR_DEMOS: Record<string, ComponentType> = {
  'effector:scatter': ScatterDemo,
  'effector:grow': GrowDemo,
  'effector:twist': TwistDemo,
  'effector:fade': FadeDemo,
  'effector:magnify': MagnifyDemo,
};
